/**
 * Reunification API service for Whakahaere Safety Management System.
 * Endpoints based on ADR-064: ILUG Reunification Complete Specification.
 *
 * Typed `@tiaki/http-client` surface per ADR 113 §Decision (call-site path
 * autocomplete). The reunification surface is large (60+ endpoints with
 * `{template_public_id}` / `{event_public_id}` nesting); endpoints whose URL
 * shape is not on the OpenAPI schema fall through to `client.ky` and keep the
 * lib's auth-refresh + GPC + vendor-org middleware.
 *
 * Reunification flows survive backend-initiated JWT rotation through the WS
 * `onTokenRefreshComplete` listener wiring in `SocketContext.tsx` (ADR 113
 * §"Why this matters now"); the typed call sites do not change that.
 */
import { client } from '../lib/http-client';
import { getSmsAccessToken } from '../lib/sms-auth';
import type {
  PlanTemplateDetail,
  PlanTemplateListResponse,
  PlanTemplateWrite,
  PlanTemplateFilters,
  PlanRoleRequirement,
  PlanRoleRequirementWrite,
  PlanStationRequirement,
  PlanStationRequirementWrite,
  PlanLabelTemplate,
  PlanLabelTemplateWrite,
  PlanApplicableSite,
  PlanApplicableSiteWrite,
  ReunificationSiteDetail,
  ReunificationSiteListResponse,
  SiteFilters,
  ReunificationEvent,
  ReunificationEventDetail,
  ReunificationEventListResponse,
  ReunificationEventFilters,
  StudentListResponse,
  StudentFilters,
  StudentReunification,
  GuardianListResponse,
  GuardianFilters,
  GuardianReunification,
  GuardianReunificationWrite,
  StaffAssignmentListResponse,
  StaffFilters,
  EventStaffAssignment,
  EventStaffAssignmentWrite,
  StudentReunificationWrite,
  PlanningStudentListResponse,
  PlanningStudentFilters,
  PlanStaffAssignment,
  PlanStaffAssignmentWrite,
  PlanStaffAssignmentFilters,
  PlanStaffAssignmentListResponse,
  PlanApplicableSiteWithPlan,
  ReadinessBreakdown,
  EventLogListResponse,
  EventLogFilters,
  EventMetrics,
  TimelineEntry,
  DistrictReadinessItem,
  ReunificationReport,
  ReportSection,
  ConfigurableType,
  ConfigurableTypeWrite,
  TypeCategory,
  ReunificationNote,
  ReunificationNoteWrite,
  ReunificationNoteFilters,
  NoteType,
  PlanFloorplan,
  PlanFloorplanSummary,
  PlanFloorplanWrite,
  PlanStation,
  PlanStationWrite,
  PlanZone,
  PlanZoneWrite,
  PlanFlow,
  PlanFlowWrite,
} from '../types/ReunificationTypes';

const BASE = 'api/v2/reunification';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Pass-through to `client.ky` for endpoints whose URL shape is not on the
 * OpenAPI schema.
 */
async function kyGet<T>(path: string): Promise<T> {
  const response = await client.ky.get(path);
  return (await response.json()) as T;
}
async function kyPost<T>(path: string, body?: unknown): Promise<T> {
  const response = await client.ky.post(path, body !== undefined ? { json: body } : {});
  return (await response.json()) as T;
}
async function kyPatch<T>(path: string, body: unknown): Promise<T> {
  const response = await client.ky.patch(path, { json: body });
  return (await response.json()) as T;
}
async function kyDelete(path: string): Promise<void> {
  await client.ky.delete(path);
}

// =============================================================================
// Domain 1: Plan Templates
// =============================================================================

export async function getPlanTemplates(
  filters: PlanTemplateFilters = {},
): Promise<PlanTemplateListResponse> {
  const result = await client.GET('/api/v2/reunification/templates/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as PlanTemplateListResponse;
}

export async function getPlanTemplateById(
  id: string,
): Promise<PlanTemplateDetail> {
  const result = await client.GET('/api/v2/reunification/templates/{public_id}/', {
    params: { path: { public_id: id } as never },
  });
  return unwrap(result) as unknown as PlanTemplateDetail;
}

export async function createPlanTemplate(
  data: PlanTemplateWrite,
): Promise<PlanTemplateDetail> {
  const result = await client.POST('/api/v2/reunification/templates/', {
    body: data as never,
  });
  return unwrap(result) as unknown as PlanTemplateDetail;
}

export async function updatePlanTemplate(
  id: string,
  data: Partial<PlanTemplateWrite>,
): Promise<PlanTemplateDetail> {
  const result = await client.PATCH('/api/v2/reunification/templates/{public_id}/', {
    params: { path: { public_id: id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as PlanTemplateDetail;
}

export async function deletePlanTemplate(id: string): Promise<void> {
  const result = await client.DELETE('/api/v2/reunification/templates/{public_id}/', {
    params: { path: { public_id: id } as never },
  });
  unwrap(result);
}

export async function clonePlanTemplate(
  id: string,
  name?: string,
): Promise<PlanTemplateDetail> {
  // Backend accepts an optional `name` in the request body; if omitted, the
  // server falls back to "{original name} (Copy)". Pass `name` so callers
  // (e.g. AddPlanDialog cloning the ILUG system template) can name the
  // resulting district-scoped plan themselves.
  const result = await client.POST('/api/v2/reunification/templates/{public_id}/clone/', {
    params: { path: { public_id: id } as never },
    body: (name ? { name } : {}) as never,
  });
  return unwrap(result) as unknown as PlanTemplateDetail;
}

export async function publishPlanTemplate(
  id: string,
): Promise<PlanTemplateDetail> {
  const result = await client.POST('/api/v2/reunification/templates/{public_id}/publish/', {
    params: { path: { public_id: id } as never },
    body: {} as never,
  });
  return unwrap(result) as unknown as PlanTemplateDetail;
}

export async function unpublishPlanTemplate(
  id: string,
): Promise<PlanTemplateDetail> {
  const result = await client.POST('/api/v2/reunification/templates/{public_id}/unpublish/', {
    params: { path: { public_id: id } as never },
    body: {} as never,
  });
  return unwrap(result) as unknown as PlanTemplateDetail;
}

// =============================================================================
// Domain 1: Plan Role Requirements (nested under templates)
// =============================================================================

export async function getRoleRequirements(
  templateId: string,
): Promise<PlanRoleRequirement[]> {
  const result = await client.GET(
    '/api/v2/reunification/templates/{template_public_id}/role-requirements/',
    { params: { path: { template_public_id: templateId } as never } },
  );
  return unwrap(result) as unknown as PlanRoleRequirement[];
}

export async function createRoleRequirement(
  templateId: string,
  data: PlanRoleRequirementWrite,
): Promise<PlanRoleRequirement> {
  const result = await client.POST(
    '/api/v2/reunification/templates/{template_public_id}/role-requirements/',
    {
      params: { path: { template_public_id: templateId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanRoleRequirement;
}

export async function updateRoleRequirement(
  templateId: string,
  id: string,
  data: Partial<PlanRoleRequirementWrite>,
): Promise<PlanRoleRequirement> {
  const result = await client.PATCH(
    '/api/v2/reunification/templates/{template_public_id}/role-requirements/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: id } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanRoleRequirement;
}

export async function deleteRoleRequirement(
  templateId: string,
  id: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/templates/{template_public_id}/role-requirements/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: id } as never,
      },
    },
  );
  unwrap(result);
}

// =============================================================================
// Domain 1: Plan Station Requirements (nested under templates)
// =============================================================================

export async function getStationRequirements(
  templateId: string,
): Promise<PlanStationRequirement[]> {
  const result = await client.GET(
    '/api/v2/reunification/templates/{template_public_id}/station-requirements/',
    { params: { path: { template_public_id: templateId } as never } },
  );
  return unwrap(result) as unknown as PlanStationRequirement[];
}

export async function createStationRequirement(
  templateId: string,
  data: PlanStationRequirementWrite,
): Promise<PlanStationRequirement> {
  const result = await client.POST(
    '/api/v2/reunification/templates/{template_public_id}/station-requirements/',
    {
      params: { path: { template_public_id: templateId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanStationRequirement;
}

export async function updateStationRequirement(
  templateId: string,
  id: string,
  data: Partial<PlanStationRequirementWrite>,
): Promise<PlanStationRequirement> {
  const result = await client.PATCH(
    '/api/v2/reunification/templates/{template_public_id}/station-requirements/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: id } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanStationRequirement;
}

export async function deleteStationRequirement(
  templateId: string,
  id: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/templates/{template_public_id}/station-requirements/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: id } as never,
      },
    },
  );
  unwrap(result);
}

// =============================================================================
// Domain 1: Plan Floorplans + stations/zones/flows (abstract floorplan editor)
// Reads come from the floorplan detail (stations/zones/flows are nested);
// writes go through the per-entity nested routers.
// =============================================================================

export async function getPlanFloorplans(
  templateId: string,
): Promise<PlanFloorplanSummary[]> {
  const result = await client.GET(
    '/api/v2/reunification/templates/{template_public_id}/floorplans/',
    { params: { path: { template_public_id: templateId } as never } },
  );
  return unwrap(result) as unknown as PlanFloorplanSummary[];
}

export async function getPlanFloorplan(
  templateId: string,
  floorplanId: string,
): Promise<PlanFloorplan> {
  const result = await client.GET(
    '/api/v2/reunification/templates/{template_public_id}/floorplans/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: floorplanId } as never,
      },
    },
  );
  return unwrap(result) as unknown as PlanFloorplan;
}

export async function createPlanFloorplan(
  templateId: string,
  data: PlanFloorplanWrite,
): Promise<PlanFloorplan> {
  const result = await client.POST(
    '/api/v2/reunification/templates/{template_public_id}/floorplans/',
    {
      params: { path: { template_public_id: templateId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanFloorplan;
}

export async function updatePlanFloorplan(
  templateId: string,
  floorplanId: string,
  data: Partial<PlanFloorplanWrite>,
): Promise<PlanFloorplan> {
  const result = await client.PATCH(
    '/api/v2/reunification/templates/{template_public_id}/floorplans/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: floorplanId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanFloorplan;
}

export async function deletePlanFloorplan(
  templateId: string,
  floorplanId: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/templates/{template_public_id}/floorplans/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: floorplanId } as never,
      },
    },
  );
  unwrap(result);
}

type FloorplanChildKind = 'stations' | 'zones' | 'flows';

// Doubly-nested child routes go through client.ky with a hand-built path
// (same auth-refresh + GPC + vendor-org middleware as the typed calls).
const floorplanChildPath = (
  kind: FloorplanChildKind,
  templateId: string,
  floorplanId: string,
  id?: string,
) =>
  `${BASE}/templates/${templateId}/floorplans/${floorplanId}/${kind}/${id ? `${id}/` : ''}`;

function createFloorplanChild<T>(
  kind: FloorplanChildKind,
  templateId: string,
  floorplanId: string,
  data: unknown,
): Promise<T> {
  return kyPost<T>(floorplanChildPath(kind, templateId, floorplanId), data);
}

function updateFloorplanChild<T>(
  kind: FloorplanChildKind,
  templateId: string,
  floorplanId: string,
  id: string,
  data: unknown,
): Promise<T> {
  return kyPatch<T>(floorplanChildPath(kind, templateId, floorplanId, id), data);
}

function deleteFloorplanChild(
  kind: FloorplanChildKind,
  templateId: string,
  floorplanId: string,
  id: string,
): Promise<void> {
  return kyDelete(floorplanChildPath(kind, templateId, floorplanId, id));
}

export const createPlanStation = (
  templateId: string,
  floorplanId: string,
  data: PlanStationWrite,
) => createFloorplanChild<PlanStation>('stations', templateId, floorplanId, data);

export const updatePlanStation = (
  templateId: string,
  floorplanId: string,
  id: string,
  data: Partial<PlanStationWrite>,
) => updateFloorplanChild<PlanStation>('stations', templateId, floorplanId, id, data);

export const deletePlanStation = (
  templateId: string,
  floorplanId: string,
  id: string,
) => deleteFloorplanChild('stations', templateId, floorplanId, id);

export const createPlanZone = (
  templateId: string,
  floorplanId: string,
  data: PlanZoneWrite,
) => createFloorplanChild<PlanZone>('zones', templateId, floorplanId, data);

export const updatePlanZone = (
  templateId: string,
  floorplanId: string,
  id: string,
  data: Partial<PlanZoneWrite>,
) => updateFloorplanChild<PlanZone>('zones', templateId, floorplanId, id, data);

export const deletePlanZone = (
  templateId: string,
  floorplanId: string,
  id: string,
) => deleteFloorplanChild('zones', templateId, floorplanId, id);

export const createPlanFlow = (
  templateId: string,
  floorplanId: string,
  data: PlanFlowWrite,
) => createFloorplanChild<PlanFlow>('flows', templateId, floorplanId, data);

export const updatePlanFlow = (
  templateId: string,
  floorplanId: string,
  id: string,
  data: Partial<PlanFlowWrite>,
) => updateFloorplanChild<PlanFlow>('flows', templateId, floorplanId, id, data);

export const deletePlanFlow = (
  templateId: string,
  floorplanId: string,
  id: string,
) => deleteFloorplanChild('flows', templateId, floorplanId, id);

// =============================================================================
// Domain 1: Plan Label Templates (nested under templates)
// =============================================================================

export async function getLabelTemplates(
  templateId: string,
): Promise<PlanLabelTemplate[]> {
  const result = await client.GET(
    '/api/v2/reunification/templates/{template_public_id}/label-templates/',
    { params: { path: { template_public_id: templateId } as never } },
  );
  return unwrap(result) as unknown as PlanLabelTemplate[];
}

export async function createLabelTemplate(
  templateId: string,
  data: PlanLabelTemplateWrite,
): Promise<PlanLabelTemplate> {
  const result = await client.POST(
    '/api/v2/reunification/templates/{template_public_id}/label-templates/',
    {
      params: { path: { template_public_id: templateId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanLabelTemplate;
}

export async function updateLabelTemplate(
  templateId: string,
  id: string,
  data: Partial<PlanLabelTemplateWrite>,
): Promise<PlanLabelTemplate> {
  const result = await client.PATCH(
    '/api/v2/reunification/templates/{template_public_id}/label-templates/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: id } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanLabelTemplate;
}

export async function deleteLabelTemplate(
  templateId: string,
  id: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/templates/{template_public_id}/label-templates/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: id } as never,
      },
    },
  );
  unwrap(result);
}

// =============================================================================
// Domain 1: Applicable Sites (nested under templates)
// =============================================================================

export async function getApplicableSites(
  templateId: string,
): Promise<PlanApplicableSite[]> {
  const result = await client.GET(
    '/api/v2/reunification/templates/{template_public_id}/applicable-sites/',
    { params: { path: { template_public_id: templateId } as never } },
  );
  return unwrap(result) as unknown as PlanApplicableSite[];
}

export async function createApplicableSite(
  templateId: string,
  data: PlanApplicableSiteWrite,
): Promise<PlanApplicableSite> {
  const result = await client.POST(
    '/api/v2/reunification/templates/{template_public_id}/applicable-sites/',
    {
      params: { path: { template_public_id: templateId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanApplicableSite;
}

export async function updateApplicableSite(
  templateId: string,
  siteId: string,
  data: Partial<PlanApplicableSiteWrite>,
): Promise<PlanApplicableSite> {
  const result = await client.PATCH(
    '/api/v2/reunification/templates/{template_public_id}/applicable-sites/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: siteId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanApplicableSite;
}

export async function deleteApplicableSite(
  templateId: string,
  siteId: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/templates/{template_public_id}/applicable-sites/{public_id}/',
    {
      params: {
        path: { template_public_id: templateId, public_id: siteId } as never,
      },
    },
  );
  unwrap(result);
}

// =============================================================================
// Domain 2: Reunification Sites
// =============================================================================

export async function getReunificationSites(
  filters: SiteFilters = {},
): Promise<ReunificationSiteListResponse> {
  const result = await client.GET('/api/v2/reunification/sites/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as ReunificationSiteListResponse;
}

export async function getReunificationSiteById(
  id: string,
): Promise<ReunificationSiteDetail> {
  const result = await client.GET('/api/v2/reunification/sites/{public_id}/', {
    params: { path: { public_id: id } as never },
  });
  return unwrap(result) as unknown as ReunificationSiteDetail;
}

// =============================================================================
// Domain 3: Reunification Events
// =============================================================================

export async function getReunificationEvents(
  filters: ReunificationEventFilters = {},
): Promise<ReunificationEventListResponse> {
  // ReunificationEventViewSet has no `pagination_class`, so the backend
  // returns a flat array `[ {...} ]` — every other reunification viewset
  // is paginated. Tolerate either shape.
  const result = await client.GET('/api/v2/reunification/events/', {
    params: { query: filters as never },
  });
  const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
  const raw = result.data as
    | ReunificationEventListResponse
    | ReunificationEvent[]
    | undefined;
  if (Array.isArray(raw)) {
    return { count: raw.length, next: null, previous: null, results: raw };
  }
  return raw as ReunificationEventListResponse;
}

export async function getReunificationEventById(
  id: string,
): Promise<ReunificationEventDetail> {
  const result = await client.GET('/api/v2/reunification/events/{public_id}/', {
    params: { path: { public_id: id } as never },
  });
  return unwrap(result) as unknown as ReunificationEventDetail;
}

// =============================================================================
// Domain 3: Event Students
// =============================================================================

export async function getEventStudents(
  eventId: string,
  filters: StudentFilters = {},
): Promise<StudentListResponse> {
  const result = await client.GET(
    '/api/v2/reunification/events/{event_public_id}/students/',
    {
      params: {
        path: { event_public_id: eventId } as never,
        query: filters as never,
      },
    },
  );
  return unwrap(result) as unknown as StudentListResponse;
}

/**
 * Fetch a single student detail row.
 *
 * Backed by `StudentReunificationViewSet.retrieve()` which uses
 * `StudentReunificationDetailSerializer`. The backend writes a
 * `student.viewed` row to `EventAssignmentLog` on every successful
 * fetch — this is the FERPA access-log entry. `medical_notes` is
 * absent from the response when the requesting user lacks
 * `reunification.view_student_medical_notes`.
 */
export async function getEventStudentById(
  eventId: string,
  studentPublicId: string,
): Promise<StudentReunification> {
  const result = await client.GET(
    '/api/v2/reunification/events/{event_public_id}/students/{public_id}/',
    {
      params: {
        path: {
          event_public_id: eventId,
          public_id: studentPublicId,
        } as never,
      },
    },
  );
  return unwrap(result) as unknown as StudentReunification;
}

/**
 * Create a new StudentReunification row on an active event.
 *
 * Backed by `StudentReunificationViewSet.create()` (views.py:2113),
 * gated by `CanOperateStudentCheckIn`. The picked `roster_person` must
 * be an existing RosterPerson primary key (see `PlanningStudent.id`).
 * The backend writes a `student.added` row to `EventAssignmentLog`
 * (FERPA audit-log). `medical_notes` writes additionally require
 * `reunification.edit_student_medical_notes`; otherwise the request
 * 403s with a per-field detail.
 */
export async function createEventStudent(
  eventId: string,
  data: StudentReunificationWrite,
): Promise<StudentReunification> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/students/',
    {
      params: { path: { event_public_id: eventId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as StudentReunification;
}

/**
 * Partial-update a StudentReunification row.
 *
 * Backed by `StudentReunificationViewSet.partial_update()`. The status
 * workflow (`unaccounted` → `accounted` → `cleared_for_release` …)
 * flows through the dedicated `update_status` action, which carries
 * the WS broadcasts. This PATCH is for the writable detail fields —
 * notes / medical_notes / special_instructions / requires_leo_interview /
 * requires_counseling / supervising_staff — the writable fields on
 * `StudentReunificationSerializer`. Audit-log + medical_notes gating
 * are enforced on the backend (perform_update + validate_medical_notes).
 */
export async function updateEventStudent(
  eventId: string,
  studentPublicId: string,
  data: Partial<StudentReunificationWrite>,
): Promise<StudentReunification> {
  const result = await client.PATCH(
    '/api/v2/reunification/events/{event_public_id}/students/{public_id}/',
    {
      params: {
        path: {
          event_public_id: eventId,
          public_id: studentPublicId,
        } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as StudentReunification;
}

// =============================================================================
// Domain 3: Event Guardians
// =============================================================================

export async function getEventGuardians(
  eventId: string,
  filters: GuardianFilters = {},
): Promise<GuardianListResponse> {
  const result = await client.GET(
    '/api/v2/reunification/events/{event_public_id}/guardians/',
    {
      params: {
        path: { event_public_id: eventId } as never,
        query: filters as never,
      },
    },
  );
  return unwrap(result) as unknown as GuardianListResponse;
}

/**
 * Create a new guardian record on an active reunification event.
 *
 * Backed by `GuardianReunificationViewSet.create()` (views.py:2499), gated
 * by `CanOperateGuardianVerification`. The picked `roster_person` must be
 * an existing RosterPerson; ad-hoc walk-in guardian creation is out of
 * scope for v1 (the picker only surfaces existing roster rows — see
 * AddEventGuardianDialog).
 *
 * The backend writes an EventAssignmentLog `guardian_added` row inside
 * `perform_create` so the CCPA/CPRA privacy audit-log captures "who added
 * which guardian" with the initial values.
 */
export async function createEventGuardian(
  eventId: string,
  data: GuardianReunificationWrite,
): Promise<GuardianReunification> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/guardians/',
    {
      params: { path: { event_public_id: eventId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as GuardianReunification;
}

/**
 * Partial-update an existing guardian record.
 *
 * Editable fields: `verification_method`, `id_type`, `is_pre_verified`,
 * `authorized_students`. Status workflow fields (`status`, `arrived_at`,
 * `verified_at`, etc.) flow through the dedicated `arrived` / `check_in` /
 * `verify` / `deny` / `pre_verify` actions which carry the WS broadcast side
 * effects — a raw PATCH would skip those.
 *
 * The backend's `perform_update` hook writes a `guardian_updated`
 * EventAssignmentLog row with before/after values; the `authorized_students`
 * M2M edit is the sensitive one (adding a student to a guardian's
 * authorized list during an active event) and the audit-log captures the
 * delta so the privacy reviewer can audit who authorized what.
 */
export async function updateEventGuardian(
  eventId: string,
  guardianPublicId: string,
  data: Partial<GuardianReunificationWrite>,
): Promise<GuardianReunification> {
  const result = await client.PATCH(
    '/api/v2/reunification/events/{event_public_id}/guardians/{public_id}/',
    {
      params: {
        path: {
          event_public_id: eventId,
          public_id: guardianPublicId,
        } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as GuardianReunification;
}

// =============================================================================
// Domain 3: Event Staff
// =============================================================================

export async function getEventStaff(
  eventId: string,
  filters: StaffFilters = {},
): Promise<StaffAssignmentListResponse> {
  const result = await client.GET(
    '/api/v2/reunification/events/{event_public_id}/staff/',
    {
      params: {
        path: { event_public_id: eventId } as never,
        query: filters as never,
      },
    },
  );
  return unwrap(result) as unknown as StaffAssignmentListResponse;
}

/**
 * Create a new staff assignment on an active reunification event.
 *
 * Backed by `EventStaffAssignmentViewSet.create()` (views.py:1820), gated
 * by `CanManageStaffAssignments`. The picked `staff_member` must be an
 * existing auth user; ad-hoc staff creation is out of scope for v1 (the
 * picker only surfaces existing users — see AddEventStaffDialog).
 */
export async function createEventStaff(
  eventId: string,
  data: EventStaffAssignmentWrite,
): Promise<EventStaffAssignment> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/staff/',
    {
      params: { path: { event_public_id: eventId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as EventStaffAssignment;
}

/**
 * Partial-update an existing staff assignment (role, station, notes).
 *
 * Backed by `EventStaffAssignmentViewSet.partial_update()`. The status
 * workflow (assigned → checked_in → released) flows through the dedicated
 * `check_in` / `release` actions, not this PATCH — those carry side
 * effects (chat-thread join, WS broadcasts) that a raw status write
 * would skip.
 */
export async function updateEventStaff(
  eventId: string,
  staffPublicId: string,
  data: Partial<EventStaffAssignmentWrite>,
): Promise<EventStaffAssignment> {
  const result = await client.PATCH(
    '/api/v2/reunification/events/{event_public_id}/staff/{public_id}/',
    {
      params: {
        path: { event_public_id: eventId, public_id: staffPublicId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as EventStaffAssignment;
}

// =============================================================================
// Pre-Event Planning: Students (Roster)
// =============================================================================

export async function getPlanningStudents(
  filters: PlanningStudentFilters = {},
): Promise<PlanningStudentListResponse> {
  const result = await client.GET('/api/v2/reunification/students/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as PlanningStudentListResponse;
}

// =============================================================================
// Domain 4: Staff Role Pre-Assignments
// =============================================================================

export async function getPlanStaffAssignments(
  filters: PlanStaffAssignmentFilters = {},
): Promise<PlanStaffAssignmentListResponse> {
  const result = await client.GET('/api/v2/reunification/staff-roles/', {
    params: { query: filters as never },
  });
  const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
  const data = result.data as
    | PlanStaffAssignmentListResponse
    | PlanStaffAssignment[]
    | undefined;
  if (Array.isArray(data)) {
    return { count: data.length, next: null, previous: null, results: data };
  }
  return data as PlanStaffAssignmentListResponse;
}

export async function createPlanStaffAssignment(
  data: PlanStaffAssignmentWrite,
): Promise<PlanStaffAssignment> {
  const result = await client.POST('/api/v2/reunification/staff-roles/', {
    body: data as never,
  });
  return unwrap(result) as unknown as PlanStaffAssignment;
}

export async function updatePlanStaffAssignment(
  id: string,
  data: Partial<PlanStaffAssignmentWrite>,
): Promise<PlanStaffAssignment> {
  const result = await client.PATCH(
    '/api/v2/reunification/staff-roles/{public_id}/',
    {
      params: { path: { public_id: id } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as PlanStaffAssignment;
}

export async function deletePlanStaffAssignment(id: string): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/staff-roles/{public_id}/',
    { params: { path: { public_id: id } as never } },
  );
  unwrap(result);
}

// =============================================================================
// Concrete Plan: Generate / Activate / Readiness
// =============================================================================

export async function generateConcretePlan(
  templateId: string,
  siteId: string,
): Promise<PlanApplicableSiteWithPlan> {
  const result = await client.POST(
    '/api/v2/reunification/templates/{template_public_id}/applicable-sites/{public_id}/generate/',
    {
      params: {
        path: { template_public_id: templateId, public_id: siteId } as never,
      },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as PlanApplicableSiteWithPlan;
}

export async function activateConcretePlan(
  templateId: string,
  siteId: string,
  alertId: number,
): Promise<ReunificationEventDetail> {
  const result = await client.POST(
    '/api/v2/reunification/templates/{template_public_id}/applicable-sites/{public_id}/activate/',
    {
      params: {
        path: { template_public_id: templateId, public_id: siteId } as never,
      },
      body: { alert_id: alertId } as never,
    },
  );
  return unwrap(result) as unknown as ReunificationEventDetail;
}

export async function getConcretePlanReadiness(
  templateId: string,
  siteId: string,
): Promise<ReadinessBreakdown> {
  const result = await client.GET(
    '/api/v2/reunification/templates/{template_public_id}/applicable-sites/{public_id}/readiness/',
    {
      params: {
        path: { template_public_id: templateId, public_id: siteId } as never,
      },
    },
  );
  return unwrap(result) as unknown as ReadinessBreakdown;
}

/**
 * Activate reunification for an existing alert.
 */
export async function activateReunificationForAlert(
  alertId: number,
  data: { plan_template_id?: string } = {},
): Promise<ReunificationEventDetail> {
  const result = await client.POST(
    '/api/v2/reunification/events/activate-for-alert/{alert_id}/',
    {
      params: { path: { alert_id: alertId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as ReunificationEventDetail;
}

// =============================================================================
// Domain 3: Event Lifecycle Actions
// =============================================================================

export async function pauseEvent(
  eventId: string,
): Promise<ReunificationEventDetail> {
  const result = await client.POST(
    '/api/v2/reunification/events/{public_id}/pause/',
    {
      params: { path: { public_id: eventId } as never },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as ReunificationEventDetail;
}

export async function resumeEvent(
  eventId: string,
): Promise<ReunificationEventDetail> {
  const result = await client.POST(
    '/api/v2/reunification/events/{public_id}/resume/',
    {
      params: { path: { public_id: eventId } as never },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as ReunificationEventDetail;
}

export async function completeEvent(
  eventId: string,
): Promise<ReunificationEventDetail> {
  const result = await client.POST(
    '/api/v2/reunification/events/{public_id}/complete/',
    {
      params: { path: { public_id: eventId } as never },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as ReunificationEventDetail;
}

// =============================================================================
// Domain 3: Student Status Management
// =============================================================================

export async function updateStudentStatus(
  eventId: string,
  studentId: string,
  data: { status: string },
): Promise<StudentReunification> {
  const result = await client.PATCH(
    '/api/v2/reunification/events/{event_public_id}/students/{public_id}/',
    {
      params: {
        path: { event_public_id: eventId, public_id: studentId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as StudentReunification;
}

export async function bulkAccountStudents(
  eventId: string,
  data: { student_ids?: string[] } = {},
): Promise<{ updated_count: number }> {
  // Schema URL is `bulk_account` (underscore) — original code used `bulk-account`.
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/students/bulk_account/',
    {
      params: { path: { event_public_id: eventId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as { updated_count: number };
}

export async function releaseStudent(
  eventId: string,
  studentId: string,
  data: { guardian_id: string; notes?: string },
): Promise<StudentReunification> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/students/{public_id}/release/',
    {
      params: {
        path: { event_public_id: eventId, public_id: studentId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as StudentReunification;
}

// =============================================================================
// Domain 3: Guardian Check-in / Verification
// =============================================================================

export async function checkInGuardian(
  eventId: string,
  guardianId: string,
): Promise<GuardianReunification> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/guardians/{public_id}/check_in/',
    {
      params: {
        path: { event_public_id: eventId, public_id: guardianId } as never,
      },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as GuardianReunification;
}

export async function verifyGuardian(
  eventId: string,
  guardianId: string,
  data: { id_type?: string; id_number?: string },
): Promise<GuardianReunification> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/guardians/{public_id}/verify/',
    {
      params: {
        path: { event_public_id: eventId, public_id: guardianId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as GuardianReunification;
}

export async function denyGuardian(
  eventId: string,
  guardianId: string,
  data: { reason?: string },
): Promise<GuardianReunification> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/guardians/{public_id}/deny/',
    {
      params: {
        path: { event_public_id: eventId, public_id: guardianId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as GuardianReunification;
}

// =============================================================================
// Domain 3: Staff Check-in / Release
// =============================================================================

export async function checkInStaff(
  eventId: string,
  staffId: string,
): Promise<EventStaffAssignment> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/staff/{public_id}/check_in/',
    {
      params: {
        path: { event_public_id: eventId, public_id: staffId } as never,
      },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as EventStaffAssignment;
}

export async function releaseStaff(
  eventId: string,
  staffId: string,
): Promise<EventStaffAssignment> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/staff/{public_id}/release/',
    {
      params: {
        path: { event_public_id: eventId, public_id: staffId } as never,
      },
      body: {} as never,
    },
  );
  return unwrap(result) as unknown as EventStaffAssignment;
}

// =============================================================================
// Domain 3: Event Audit Logs
// =============================================================================

export async function getEventLogs(
  eventId: string,
  filters?: EventLogFilters,
): Promise<EventLogListResponse> {
  // Backend action is `audit_log` — a DRF-paginated envelope
  // ({count, next, previous, results}), NOT a flat array. Only `page` and
  // `page_size` are honoured; the action hardcodes `-timestamp` ordering.
  const params = new URLSearchParams();
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.page_size) params.set('page_size', String(filters.page_size));
  const query = params.toString();

  return kyGet<EventLogListResponse>(
    `${BASE}/events/${eventId}/audit_log/${query ? `?${query}` : ''}`,
  );
}

// =============================================================================
// Reporting & Analytics
// =============================================================================

export async function getEventMetrics(eventId: string): Promise<EventMetrics> {
  const result = await client.GET(
    '/api/v2/reunification/events/{public_id}/metrics/',
    { params: { path: { public_id: eventId } as never } },
  );
  return unwrap(result) as unknown as EventMetrics;
}

export async function getEventTimeline(
  eventId: string,
): Promise<TimelineEntry[]> {
  const result = await client.GET(
    '/api/v2/reunification/events/{public_id}/timeline/',
    { params: { path: { public_id: eventId } as never } },
  );
  return unwrap(result) as unknown as TimelineEntry[];
}

export async function getDistrictReadiness(): Promise<DistrictReadinessItem[]> {
  // `/readiness/district/` returns the flat per-school readiness rows for the
  // Readiness tab (distinct from `events/district_readiness/`, the district-wide
  // assessment); called via client.ky.
  return kyGet<DistrictReadinessItem[]>(`${BASE}/readiness/district/`);
}

/**
 * Fetch a reunification event report for inline display.
 *
 * Wraps the ``generate_report`` POST action — used by ``EventReportPanel``
 * to populate the inline ``event_summary`` + ``performance_metrics`` cards.
 * Defaults to ``include_pii: false`` so PII is never fetched without an
 * explicit caller decision (ADR-099 §Layer 5 / FERPA gate).
 *
 * The ``download_report`` GET sibling (below) handles the file-attachment
 * flow per ADR-064 §1559 — after-action reports are exports, not entities.
 *
 */
export async function generateEventReport(
  eventId: string,
  options: {
    include_sections?: ReportSection[];
    include_pii?: boolean;
    redact_sensitive?: boolean;
    time_zone?: string;
  } = {},
): Promise<ReunificationReport> {
  // The backend POST expects a JSON body; it is cast through ``unknown`` to
  // the generated request type. The body shape matches
  // ``ReportGenerationSerializer`` server-side.
  const result = await client.POST(
    '/api/v2/reunification/events/{public_id}/generate_report/',
    {
      params: { path: { public_id: eventId } as never },
      body: {
        include_sections:
          options.include_sections ?? ['executive_summary', 'metrics'],
        include_pii: options.include_pii ?? false,
        redact_sensitive: options.redact_sensitive ?? true,
        time_zone: options.time_zone ?? 'UTC',
      } as unknown as never,
    },
  );
  return unwrap(result) as unknown as ReunificationReport;
}

/**
 * Download a reunification event report as a JSON file.
 *
 * Mirrors the ``downloadIncidentExport`` pattern in
 * ``alerts.service.ts`` — calls ``client.downloadFile`` which preserves
 * the server-emitted filename from ``Content-Disposition``, then triggers
 * a synthetic anchor click to save the blob locally.
 *
 * When ``include_pii=true``, the backend writes an ``EventAssignmentLog``
 * row with ``action="report.exported_with_pii"`` per ADR-099 §Layer 5
 * (FERPA / export provenance). The UI must surface a confirmation gate
 * before flipping this flag on.
 *
 * Returns ``true`` on a non-empty file, ``false`` if the server returned
 * an empty body (matches ``downloadIncidentExport``'s contract).
 *
 */
export async function downloadEventReport(
  eventId: string,
  options: {
    include_sections?: ReportSection[];
    include_pii?: boolean;
    redact_sensitive?: boolean;
    time_zone?: string;
  } = {},
): Promise<boolean> {
  const params = new URLSearchParams();
  if (options.include_sections && options.include_sections.length > 0) {
    params.append('include_sections', options.include_sections.join(','));
  }
  if (options.include_pii !== undefined) {
    params.append('include_pii', String(options.include_pii));
  }
  if (options.redact_sensitive !== undefined) {
    params.append('redact_sensitive', String(options.redact_sensitive));
  }
  if (options.time_zone) {
    params.append('time_zone', options.time_zone);
  }
  const qs = params.toString();
  const path = `api/v2/reunification/events/${eventId}/download_report/${qs ? `?${qs}` : ''}`;
  const fallback = `reunification-${eventId}.json`;

  const { blob, filename } = await client.downloadFile(
    path,
    { method: 'GET' },
    fallback,
  );
  if (blob.size === 0) return false;

  const typedBlob = new Blob([blob], { type: 'application/json' });
  const url = window.URL.createObjectURL(typedBlob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute(
    'download',
    filename.endsWith('.json') ? filename : `${filename}.json`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
}

export async function exportEvent(
  eventId: string,
  format: string,
): Promise<Blob> {
  const baseURL = window.__ENV?.NX_API_URL || '';
  const token = getSmsAccessToken();
  const url = `${baseURL}/api/v2${'/reunification'}/events/${eventId}/export/?format=${encodeURIComponent(format)}`;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `JWT ${token}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }
  return response.blob();
}

// =============================================================================
// Configurable Types
// =============================================================================

export async function getConfigurableTypes(
  typeCategory: TypeCategory,
): Promise<ConfigurableType[]> {
  const result = await client.GET(
    '/api/v2/reunification/configurable-types/{type_category}/',
    { params: { path: { type_category: typeCategory } as never } },
  );
  return unwrap(result) as unknown as ConfigurableType[];
}
export async function getConfigurableTypeById(
  typeCategory: TypeCategory,
  id: string,
): Promise<ConfigurableType> {
  const result = await client.GET(
    '/api/v2/reunification/configurable-types/{type_category}/{public_id}/',
    {
      params: {
        path: { type_category: typeCategory, public_id: id } as never,
      },
    },
  );
  return unwrap(result) as unknown as ConfigurableType;
}

export async function createConfigurableType(
  typeCategory: TypeCategory,
  data: ConfigurableTypeWrite,
): Promise<ConfigurableType> {
  const result = await client.POST(
    '/api/v2/reunification/configurable-types/{type_category}/',
    {
      params: { path: { type_category: typeCategory } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as ConfigurableType;
}

export async function updateConfigurableType(
  typeCategory: TypeCategory,
  id: string,
  data: Partial<ConfigurableTypeWrite>,
): Promise<ConfigurableType> {
  const result = await client.PATCH(
    '/api/v2/reunification/configurable-types/{type_category}/{public_id}/',
    {
      params: {
        path: { type_category: typeCategory, public_id: id } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as ConfigurableType;
}

export async function deleteConfigurableType(
  typeCategory: TypeCategory,
  id: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/configurable-types/{type_category}/{public_id}/',
    {
      params: {
        path: { type_category: typeCategory, public_id: id } as never,
      },
    },
  );
  unwrap(result);
}

// =============================================================================
// Reunification Notes (ADR-121 P3)
// =============================================================================

/**
 * NoteType lookup — populates the notes panel's type selector.
 *
 * Backed by `NoteTypeViewSet` (mounted at
 * `/configurable-types/note-types/` before the dynamic catch-all,
 * so the URL stem reads as a generic `ConfigurableType` route from
 * a caller's perspective). The typed response shape includes
 * `visibility_tier` (lowercase enum) + `visibility_permission`,
 * letting the UI render a sensitivity badge per row without a
 * second roundtrip.
 *
 * Restricted note types are NOT filtered out client-side — the
 * server lists every type the caller can *see*; visibility on
 * the notes themselves is enforced when the notes list endpoint
 * is fetched.
 */
export async function getNoteTypes(): Promise<NoteType[]> {
  const result = await client.GET(
    '/api/v2/reunification/configurable-types/note-types/',
    {},
  );
  return unwrap(result) as unknown as NoteType[];
}

/**
 * List notes for a reunification event, optionally filtered by
 * subject (student/staff/guardian) and/or note type.
 *
 * Backed by `ReunificationNoteViewSet.list()`. The viewset's
 * queryset filter omits any note whose type is non-public and
 * whose `visibility_permission` the caller lacks — restricted
 * notes are simply ABSENT from the payload. There is no
 * client-side gating to perform; render whatever comes back.
 */
export async function getReunificationNotes(
  eventId: string,
  filters: ReunificationNoteFilters = {},
): Promise<ReunificationNote[]> {
  const result = await client.GET(
    '/api/v2/reunification/events/{event_public_id}/notes/',
    {
      params: {
        path: { event_public_id: eventId } as never,
        query: filters as never,
      },
    },
  );
  return unwrap(result) as unknown as ReunificationNote[];
}

/**
 * Create a new note on a reunification event. Exactly one of
 * `student` / `staff` / `guardian` must be set per the backend's
 * subject XOR CheckConstraint; the typed `ReunificationNoteWrite`
 * shape declares all three optional, so callers must enforce the
 * one-and-only-one invariant at the call site (the notes panel
 * sets the subject from its `subject` prop and never lets the
 * operator pick a different subject).
 *
 * A write of a non-public type without the matching
 * `change_<code>_note` permission is rejected server-side with
 * 403 — the UI surfaces that as a toast and leaves the form open.
 */
export async function createReunificationNote(
  eventId: string,
  data: ReunificationNoteWrite,
): Promise<ReunificationNote> {
  const result = await client.POST(
    '/api/v2/reunification/events/{event_public_id}/notes/',
    {
      params: { path: { event_public_id: eventId } as never },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as ReunificationNote;
}

/**
 * PATCH an existing note's `body` (and/or `note_type`, though the
 * panel only exposes body editing today). The same write-side
 * permission check applies — editing a note of a non-public type
 * requires the matching `change_<code>_note` permission, even if
 * the caller has read access via `view_<code>_note`.
 */
export async function updateReunificationNote(
  eventId: string,
  noteId: string,
  data: Partial<ReunificationNoteWrite>,
): Promise<ReunificationNote> {
  const result = await client.PATCH(
    '/api/v2/reunification/events/{event_public_id}/notes/{public_id}/',
    {
      params: {
        path: { event_public_id: eventId, public_id: noteId } as never,
      },
      body: data as never,
    },
  );
  return unwrap(result) as unknown as ReunificationNote;
}

/**
 * DELETE a note. Subject to the same permission gate as edit —
 * removing a non-public note requires the matching
 * `change_<code>_note` permission.
 */
export async function deleteReunificationNote(
  eventId: string,
  noteId: string,
): Promise<void> {
  const result = await client.DELETE(
    '/api/v2/reunification/events/{event_public_id}/notes/{public_id}/',
    {
      params: {
        path: { event_public_id: eventId, public_id: noteId } as never,
      },
    },
  );
  unwrap(result);
}
